<template>
  <div
    class="base-section"
    v-bind="$attrs"
    :class="{ 'base-section-min-height': showError }"
  >
    <div
      :class="{
        'base-section__header': true,
        'base-section__header-has-warning': showHeaderWarning,
      }"
    >
      <div class="flex flex-row justify-between">
        <span class="type-heading-sm font-bold text-text-primary">{{
          props.heading
        }}</span>
        <div
          class="flex select-none items-center gap-xs font-normal text-neutral-500"
          v-if="state.hasHelpText"
        >
          Hjälptexter
          <ToggleSwitch
            :checked="state.showHelpText"
            @change="toggleHelpText"
          />
        </div>
      </div>
    </div>

    <template v-if="showHeaderWarning">
      <div v-for="(status, index) in props.infoStatus" :key="index">
        <div
          v-if="status.status !== DataStatus.Success"
          className="base-section__status-warning-content"
        >
          <div class="base-section__status-warning-content-icon">
            <avtalat-icon
              id="varning"
              :size="20"
              color="feedback-alert-darker"
            />
          </div>
          <div class="base-section__status-warning-content-text">
            Uppgifter gick inte att hämta från {{ status.source }}.
            <a v-on:click="toggleModal()" href="#">Visa detaljer</a>
          </div>
        </div>
      </div>
    </template>

    <div
      class="base-section-content"
      :class="props.showError ? 'blurry-section' : ''"
    >
      <slot></slot>
    </div>

    <avtalat-message
      class="base-section__error-dialog"
      v-if="props.showError"
      type="negative"
      heading="Kunde inte hämta informationen"
    >
      <p class="base-section__error-dialog-content">
        {{ props.errorMessage || "Något gick fel." }}
      </p>
      <avtalat-button icon="uppdatering" @click="props.onRetry" outlined
        >Försök igen</avtalat-button
      >
    </avtalat-message>
  </div>

  <avtalat-dialog
    v-if="state.showModal"
    icon="fragetecken"
    :heading="props.heading"
    :closeFn="toggleModal"
  >
    <div v-for="(status, index) in props.infoStatus" :key="index">
      <div className="base-section__status-dialog-item">
        <div
          class="text-feedback-alert-darker"
          v-if="status.status !== DataStatus.Success"
        >
          <avtalat-icon id="varning" :size="20" color="feedback-alert-darker" />
          {{ getWarningText(status) }}
        </div>
        <div>
          <avtalat-icon id="sammanstallning" :size="20" color="neutral-700" />
          Datakälla:
          <span
            ><strong>{{ status.source }}</strong></span
          >
        </div>
        <div>
          <avtalat-icon id="klocka" :size="20" color="neutral-700" />
          Uppdaterad:
          <span
            ><strong>{{
              formatRelativeDate(status.updatedAtDateTime)
            }}</strong></span
          >
        </div>
        <div>
          <avtalat-icon id="utropstecken" :size="20" color="neutral-700" />
          Status:
          <span
            ><strong>{{ status.status }}</strong></span
          >
        </div>
      </div>
    </div>
  </avtalat-dialog>
</template>

<script lang="ts" setup>
import { computed, provide, reactive } from "vue";

import ToggleSwitch from "@/components/ui/ToggleSwitch.vue";
import {
  DataStatus,
  type IInfoStatus,
} from "@/composables/KundserviceAppApiClient";
import { formatRelativeDate } from "@/common/formatters";

type BaseSectionProps = {
  heading: string;
  infoStatus?: Array<IInfoStatus>;
  errorTitle?: string;
  errorMessage?: string;
  onRetry?: () => void;
  showError?: boolean;
};
const props = defineProps<BaseSectionProps>();

const state = reactive({
  showModal: false,
  hasHelpText: false,
  showHelpText: false,
});

provide("showHelpText", () => {
  state.hasHelpText = true;
  return state.showHelpText;
});

const showHeaderWarning = computed(() => {
  return (
    props.infoStatus !== undefined &&
    props.infoStatus.find((s) => s.status !== DataStatus.Success)
  );
});

const getWarningText = (infoStatus: IInfoStatus) => {
  switch (infoStatus.status) {
    case DataStatus.Error:
      return `Vi kunde inte hämta data från ${infoStatus.source}. Försök igen senare.`;
    case DataStatus.NotFound:
      return `Data saknas hos ${infoStatus.source}.`;
    case DataStatus.OldData:
      return `Data från ${infoStatus.source} är gammal data och kunde inte hämtas på grund av fel hos ${infoStatus.source}. Försök igen senare för att få uppdaterad data.`;
    default:
      return "";
  }
};

const toggleModal = () => (state.showModal = !state.showModal);
const toggleHelpText = () => (state.showHelpText = !state.showHelpText);
</script>

<style>
.base-section {
  position: relative;
  margin-bottom: 3em;
  font-size: 16px;
}

.base-section-min-height {
  min-height: 300px;
}

.base-section__header {
  margin-bottom: 1em;
  border-bottom: solid 1px;
  padding-bottom: 6px;
}

.base-section__header-has-warning {
  margin-bottom: 0;
  border-bottom: 2px solid var(--feedback-alert-darker);
}

.text-heading-sm {
  display: flex;
  justify-content: space-between;
}

.info-icon {
  cursor: pointer;
}

.warning-icon {
  cursor: pointer;
  color: var(--feedback-alert-darker);
}

.base-section__status-warning-content {
  display: flex;
  gap: 6px;
  border: 1px solid var(--neutral-200);
  border-top: 0;
  padding: 6px 6px 0px;
  font-size: 14px;
}

.base-section-content {
  position: relative;
}

.base-section__status-dialog-item {
  margin-top: 1em;
}

.base-section__error-dialog {
  position: absolute !important;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 90%;
}

.base-section__error-dialog-content {
  margin-bottom: 1em;
}

.blurry-section {
  opacity: 0.3;
}
</style>
